<template>
  <div
    v-if="open"
    ref="windowRef"
    class="serial-console-window"
    :style="windowStyle"
    @pointerdown="emit('focus')"
  >
    <div
      v-if="dragging"
      class="serial-console-window__drag-mask"
      @pointermove.prevent="handleDrag"
      @pointerup.prevent="stopDrag"
      @pointercancel.prevent="stopDrag"
    ></div>
    <div
      v-if="resizing"
      class="serial-console-window__resize-mask"
      @pointermove.prevent="handleResize"
      @pointerup.prevent="stopResize"
      @pointercancel.prevent="stopResize"
    ></div>
    <div
      class="serial-console-window__header"
      @pointerdown.prevent="startDrag"
    >
      <div class="serial-console-window__traffic-lights" @pointerdown.stop>
        <button
          type="button"
          class="serial-console-window__traffic-light is-close"
          aria-label="Close console window"
          @click.stop="closeWindow"
        ></button>
        <button
          type="button"
          class="serial-console-window__traffic-light is-minimize"
          aria-label="Minimize console window"
          @click.stop="minimizeWindow"
        ></button>
        <a
          class="serial-console-window__traffic-light is-maximize"
          :href="src"
          target="_blank"
          rel="noopener noreferrer"
          :title="`Open ${title} in new tab`"
          aria-label="Open console in new tab"
          @pointerdown.stop
          @click.stop="handlePopout"
        ></a>
      </div>
      <div class="serial-console-window__title">{{ title }}</div>
      <div
        v-if="currentStatusBadgeText"
        class="serial-console-window__status-badge"
        :class="`is-${currentStatusBadgeTone}`"
      >
        {{ currentStatusBadgeText }}
      </div>
    </div>

    <div
      class="serial-console-window__body"
      @pointerdown.stop="focusWindowFrame"
      @click.stop
    >
      <iframe
        v-if="src"
        ref="frameRef"
        :key="src"
        class="serial-console-window__frame"
        :src="src"
        :title="title"
        @load="focusWindowFrame"
      ></iframe>
      <div
        class="serial-console-window__resize-handle"
        @pointerdown.prevent="startResize"
      ></div>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'

const props = defineProps({
  open: { type: Boolean, default: false },
  src: { type: String, default: '' },
  title: { type: String, default: 'Console' },
  statusBadgeText: { type: String, default: '' },
  statusBadgeTone: { type: String, default: 'normal' },
  zIndex: { type: Number, default: 1080 },
  initialOffset: { type: Number, default: 0 },
  enterFrom: {
    type: Object,
    default: null
  },
  minimizeTo: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['close', 'focus', 'minimize'])

const defaultWidth = 1120
const defaultHeight = 820
const minWidth = 520
const minHeight = 320

const size = ref({
  width: defaultWidth,
  height: defaultHeight
})
const position = ref({
  left: Math.max(24, Math.round((window.innerWidth - defaultWidth) / 2)) + props.initialOffset,
  top: 12 + props.initialOffset
})
const dragging = ref(false)
const resizing = ref(false)
const frameRef = ref(null)
const windowRef = ref(null)
const minimizeAnimating = ref(false)
const liveStatusBadgeText = ref("")
const liveStatusBadgeTone = ref("")

let dragState = null
let resizeState = null

const windowStyle = computed(() => ({
  left: `${position.value.left}px`,
  top: `${position.value.top}px`,
  width: `${size.value.width}px`,
  height: `${size.value.height}px`,
  zIndex: props.zIndex
}))

const currentStatusBadgeText = computed(() => liveStatusBadgeText.value || props.statusBadgeText || "")
const currentStatusBadgeTone = computed(() => liveStatusBadgeTone.value || props.statusBadgeTone || "normal")

function clampPosition(left, top) {
  const maxLeft = Math.max(16, window.innerWidth - size.value.width - 16)
  const maxTop = Math.max(16, window.innerHeight - size.value.height - 16)
  position.value = {
    left: Math.min(Math.max(16, left), maxLeft),
    top: Math.min(Math.max(16, top), maxTop)
  }
}

function clampSize(width, height) {
  size.value = {
    width: Math.min(Math.max(minWidth, width), Math.max(minWidth, window.innerWidth - position.value.left - 16)),
    height: Math.min(Math.max(minHeight, height), Math.max(minHeight, window.innerHeight - position.value.top - 16))
  }
}

function startDrag(event) {
  if (event.button !== undefined && event.button !== 0) return

  emit('focus')
  dragState = {
    pointerId: event.pointerId,
    startX: event.clientX,
    startY: event.clientY,
    left: position.value.left,
    top: position.value.top
  }
  dragging.value = true
  event.currentTarget?.setPointerCapture?.(event.pointerId)

  window.addEventListener('pointermove', handleDrag)
  window.addEventListener('pointerup', stopDrag, true)
  window.addEventListener('pointercancel', stopDrag, true)
  window.addEventListener('blur', stopDrag)
}

function handleDrag(event) {
  if (!dragState) return
  if (dragState.pointerId != null && event.pointerId != null && dragState.pointerId !== event.pointerId) return

  clampPosition(
    dragState.left + (event.clientX - dragState.startX),
    dragState.top + (event.clientY - dragState.startY)
  )
}

function stopDrag() {
  dragState = null
  dragging.value = false
  window.removeEventListener('pointermove', handleDrag)
  window.removeEventListener('pointerup', stopDrag, true)
  window.removeEventListener('pointercancel', stopDrag, true)
  window.removeEventListener('blur', stopDrag)
}

function startResize(event) {
  if (event.button !== undefined && event.button !== 0) return

  emit('focus')
  resizeState = {
    pointerId: event.pointerId,
    startX: event.clientX,
    startY: event.clientY,
    width: size.value.width,
    height: size.value.height
  }
  resizing.value = true
  event.currentTarget?.setPointerCapture?.(event.pointerId)

  window.addEventListener('pointermove', handleResize)
  window.addEventListener('pointerup', stopResize, true)
  window.addEventListener('pointercancel', stopResize, true)
  window.addEventListener('blur', stopResize)
}

function handleResize(event) {
  if (!resizeState) return
  if (resizeState.pointerId != null && event.pointerId != null && resizeState.pointerId !== event.pointerId) return

  clampSize(
    resizeState.width + (event.clientX - resizeState.startX),
    resizeState.height + (event.clientY - resizeState.startY)
  )
}

function stopResize() {
  resizeState = null
  resizing.value = false
  window.removeEventListener('pointermove', handleResize)
  window.removeEventListener('pointerup', stopResize, true)
  window.removeEventListener('pointercancel', stopResize, true)
  window.removeEventListener('blur', stopResize)
}

function closeWindow() {
  emit('close')
}

function minimizeWindow() {
  if (minimizeAnimating.value) {
    return
  }

  if (!props.minimizeTo || !windowRef.value) {
    emit('minimize')
    return
  }

  minimizeAnimating.value = true
  animateToPoint(
    props.minimizeTo,
    {
      duration: 240,
      easing: 'cubic-bezier(0.24, 0.78, 0.18, 1.0)',
      startScale: 1,
      endScale: 0.16,
      startOpacity: 1,
      endOpacity: 0.2,
      reverse: false
    },
    () => {
      minimizeAnimating.value = false
      emit('minimize')
    }
  )
}

function focusWindowFrame() {
  emit('focus')
  try {
    frameRef.value?.contentWindow?.focus?.()
  } catch (error) {
    // Ignore cross-document focus failures.
  }
}

function handlePopout() {
  emit('close')
}

function applyLiveStatus(status) {
  if (!status || typeof status !== "object") {
    return
  }
  liveStatusBadgeText.value = status.badgeText || ""
  liveStatusBadgeTone.value = status.badgeTone || "normal"
}

function handleConsoleStatusMessage(event) {
  if (!frameRef.value || event.source !== frameRef.value.contentWindow) {
    return
  }
  const payload = event.data
  if (!payload || payload.type !== "scc:console-status") {
    return
  }
  applyLiveStatus(payload)
}

function animateFromPoint(point) {
  if (!point || !windowRef.value) {
    return
  }

  animateToPoint(point, {
    duration: 260,
    easing: 'cubic-bezier(0.2, 0.92, 0.2, 1)',
    startScale: 0.2,
    endScale: 1,
    startOpacity: 0.24,
    endOpacity: 1,
    reverse: true
  })
}

function animateToPoint(point, options = {}, onFinish = null) {
  if (!windowRef.value || !point) {
    onFinish?.()
    return
  }

  const rect = windowRef.value.getBoundingClientRect()
  const centerX = rect.left + (rect.width / 2)
  const centerY = rect.top + (rect.height / 2)
  const dx = point.x - centerX
  const dy = point.y - centerY
  const reverse = !!options.reverse

  const fromTransform = reverse
    ? `translate(${dx}px, ${dy}px) scale(${options.startScale ?? 0.2})`
    : `translate(0px, 0px) scale(${options.startScale ?? 1})`
  const toTransform = reverse
    ? `translate(0px, 0px) scale(${options.endScale ?? 1})`
    : `translate(${dx}px, ${dy}px) scale(${options.endScale ?? 0.2})`
  const fromOpacity = reverse ? (options.startOpacity ?? 0.24) : (options.startOpacity ?? 1)
  const toOpacity = reverse ? (options.endOpacity ?? 1) : (options.endOpacity ?? 0.2)

  const animation = windowRef.value.animate(
    [
      { transform: fromTransform, opacity: fromOpacity },
      { transform: toTransform, opacity: toOpacity }
    ],
    {
      duration: options.duration || 220,
      easing: options.easing || 'ease-out',
      fill: 'forwards'
    }
  )

  const cleanup = () => {
    if (!windowRef.value) {
      onFinish?.()
      return
    }
    windowRef.value.style.transform = ''
    windowRef.value.style.opacity = ''
    onFinish?.()
  }

  animation.addEventListener('finish', cleanup, { once: true })
  animation.addEventListener('cancel', cleanup, { once: true })
}

watch(() => props.open, (value) => {
  if (value) {
    clampSize(size.value.width, size.value.height)
    clampPosition(position.value.left, position.value.top)
  }
})

onBeforeUnmount(() => {
  stopDrag()
  stopResize()
  window.removeEventListener("message", handleConsoleStatusMessage)
})

onMounted(() => {
  animateFromPoint(props.enterFrom)
  applyLiveStatus({
    badgeText: props.statusBadgeText,
    badgeTone: props.statusBadgeTone
  })
  window.addEventListener("message", handleConsoleStatusMessage)
})

watch(() => props.enterFrom, (nextPoint) => {
  animateFromPoint(nextPoint)
})

watch(() => props.src, () => {
  applyLiveStatus({
    badgeText: props.statusBadgeText,
    badgeTone: props.statusBadgeTone
  })
})
</script>

<style scoped>
.serial-console-window {
  position: fixed;
  z-index: 1080;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 16px;
  background: #0b1220;
  box-shadow: 0 24px 64px rgba(4, 10, 22, 0.4);
}

.serial-console-window__drag-mask {
  position: fixed;
  inset: 0;
  z-index: 1;
  cursor: move;
  background: transparent;
}

.serial-console-window__resize-mask {
  position: fixed;
  inset: 0;
  z-index: 1;
  cursor: nwse-resize;
  background: transparent;
}

.serial-console-window__header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 10px 14px;
  border-bottom: 1px solid rgba(132, 156, 206, 0.18);
  background: linear-gradient(180deg, rgba(25, 35, 57, 0.96), rgba(14, 20, 33, 0.96));
  cursor: move;
  user-select: none;
  position: relative;
  z-index: 2;
}

.serial-console-window__traffic-lights {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
  position: relative;
  z-index: 4;
}

.serial-console-window__traffic-light {
  width: 12px;
  height: 12px;
  border: 0;
  border-radius: 999px;
  display: inline-block;
  position: relative;
  padding: 0;
  box-shadow: inset 0 1px 1px rgba(255, 255, 255, 0.32), 0 0 0 1px rgba(0, 0, 0, 0.22);
  overflow: hidden;
}

.serial-console-window__traffic-light.is-close {
  background: #ff5f57;
  cursor: pointer;
}

.serial-console-window__traffic-light.is-minimize {
  background: #febc2e;
}

.serial-console-window__traffic-light.is-maximize {
  background: #28c840;
}

.serial-console-window__traffic-light::before,
.serial-console-window__traffic-light::after {
  content: "";
  position: absolute;
  left: 50%;
  top: 50%;
  opacity: 0;
  transition: opacity 0.16s ease, transform 0.16s ease;
}

.serial-console-window__traffic-lights:hover .serial-console-window__traffic-light::before,
.serial-console-window__traffic-lights:hover .serial-console-window__traffic-light::after,
.serial-console-window__traffic-light:focus-visible::before,
.serial-console-window__traffic-light:focus-visible::after {
  opacity: 0.78;
}

.serial-console-window__traffic-light.is-close::before,
.serial-console-window__traffic-light.is-close::after {
  width: 7px;
  height: 1.4px;
  background: rgba(84, 16, 16, 0.86);
  border-radius: 999px;
  transform-origin: center;
}

.serial-console-window__traffic-light.is-close::before {
  transform: translate(-50%, -50%) rotate(45deg);
}

.serial-console-window__traffic-light.is-close::after {
  transform: translate(-50%, -50%) rotate(-45deg);
}

.serial-console-window__traffic-light.is-minimize::before {
  width: 7px;
  height: 1.4px;
  background: rgba(89, 66, 4, 0.9);
  border-radius: 999px;
  transform: translate(-50%, -10%);
}

.serial-console-window__traffic-light.is-maximize::before,
.serial-console-window__traffic-light.is-maximize::after {
  width: 4px;
  height: 4px;
}

.serial-console-window__traffic-light.is-maximize::before {
  border-top: 1.4px solid rgba(11, 73, 22, 0.92);
  border-left: 1.4px solid rgba(11, 73, 22, 0.92);
  transform: translate(-75%, -72%);
}

.serial-console-window__traffic-light.is-maximize::after {
  border-right: 1.4px solid rgba(11, 73, 22, 0.92);
  border-bottom: 1.4px solid rgba(11, 73, 22, 0.92);
  transform: translate(-24%, -20%);
}

.serial-console-window__traffic-lights:hover .serial-console-window__traffic-light.is-maximize::before {
  transform: translate(-82%, -80%);
}

.serial-console-window__traffic-lights:hover .serial-console-window__traffic-light.is-maximize::after {
  transform: translate(-18%, -14%);
}

.serial-console-window__title {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  max-width: calc(100% - 220px);
  text-align: center;
  color: #d5e2ff;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  pointer-events: none;
}

.serial-console-window__status-badge {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 2px 9px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.01em;
  line-height: 1.2;
  border: 1px solid transparent;
  margin-left: auto;
  position: relative;
  z-index: 3;
}

.serial-console-window__status-badge.is-normal {
  color: #0f5132;
  background: #d1e7dd;
  border-color: #9fd9c0;
}

.serial-console-window__status-badge.is-warn {
  color: #664d03;
  background: #fff3cd;
  border-color: #f3d98c;
}

.serial-console-window__status-badge.is-error {
  color: #842029;
  background: #f8d7da;
  border-color: #f1b8be;
}

.serial-console-window__body {
  flex: 1 1 auto;
  min-height: 0;
  background: #0b1220;
  position: relative;
  z-index: 2;
}

.serial-console-window__frame {
  width: 100%;
  height: 100%;
  border: 0;
  display: block;
  background: #0b1220;
}

.serial-console-window__resize-handle {
  position: absolute;
  right: 0;
  bottom: 0;
  z-index: 3;
  width: 18px;
  height: 18px;
  cursor: nwse-resize;
  background:
    linear-gradient(135deg, transparent 0 40%, rgba(213, 226, 255, 0.5) 40% 48%, transparent 48% 58%, rgba(213, 226, 255, 0.5) 58% 66%, transparent 66% 100%);
}
</style>
